import Lightbox from "./lightbox";
import { initCarousel } from "./carousel";
import { createFocusTrap } from "../utils/focus-trap";
import { applyReducedMotionVideos } from "../utils/reduced-motion";
import { initLazyMedia } from "../utils/lazy-media";

export default class ProjectModal {
  constructor() {
    this.modal = null;
    this.closeButton = null;
    this.isOpen = false;
    this.lastFocusedElement = null;
    this.focusTrap = null;
    this.init();
  }

  init() {
    this.createModal();
    this.attachEventListeners();
  }

  createModal() {
    const modalHTML = `
      <div class="c-project-modal" id="project-modal" role="dialog" aria-modal="true" aria-labelledby="project-modal-title" aria-hidden="true">
        <div class="c-project-modal__header">
          <h1 class="c-project-modal__title js-project-modal-title" id="project-modal-title"></h1>
        </div>
        <button type="button" class="c-project-modal__close js-project-modal-close" aria-label="Close project">CLOSE</button>
        <div class="c-project-modal__info-overlay js-project-modal-info"></div>
        <div class="c-project-modal__container">
          <div class="c-project-modal__content js-project-modal-content">
            <!-- Content will be loaded here -->
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('project-modal');
    this.closeButton = document.querySelector('.js-project-modal-close');
    this.focusTrap = createFocusTrap(this.modal);
  }

  attachEventListeners() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Close on background click (but not on content)
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.classList.contains('c-project-modal__container')) {
          this.close();
        }
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Handle next-project navigation inside modal
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        const nextLink = e.target.closest('.c-project-next__link');
        if (!nextLink) return;
        e.preventDefault();
        const url = nextLink.getAttribute('href');
        if (url) {
          this.navigate(url);
        }
      });
    }

    // Prevent body scroll when modal is open
    if (this.modal) {
      this.modal.addEventListener('transitionend', () => {
        if (this.isOpen) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      });
    }
  }

  async loadProject(url) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract title from hidden h1 or info overlay
      const titleElement = doc.querySelector('.c-project-modal__title') || doc.querySelector('h1.u-sr-only') || doc.querySelector('h1');
      const title = titleElement?.textContent?.trim() || '';
      
      // Extract info overlay
      const infoOverlay = doc.querySelector('.c-project-modal__info-overlay');
      const infoHTML = infoOverlay ? infoOverlay.outerHTML : '';
      
      // Extract project content (excluding info overlay)
      const projectContent = doc.querySelector('.c-project-content');
      let contentHTML = '';
      
      if (projectContent) {
        // Clone to avoid modifying original
        const contentClone = projectContent.cloneNode(true);
        // Remove info overlay from clone
        const infoClone = contentClone.querySelector('.c-project-modal__info-overlay');
        if (infoClone) {
          infoClone.remove();
        }
        contentHTML = contentClone.innerHTML;
      } else {
        // Fallback: try to get main content
        const mainContent = doc.querySelector('main');
        if (mainContent) {
          contentHTML = mainContent.innerHTML;
        }
      }
      
      return {
        title: title,
        info: infoHTML,
        content: contentHTML
      };
    } catch (error) {
      console.error('Error loading project:', error);
      return { 
        title: 'Error', 
        info: '',
        content: '<div style="padding: 2rem;"><p>Error loading project content.</p></div>' 
      };
    }
  }

  async open(url) {
    if (!this.modal) return;

    this.lastFocusedElement = document.activeElement;
    this.modal.classList.add('is-active');
    this.modal.setAttribute('aria-hidden', 'false');
    this.isOpen = true;
    await this.replaceContent(url);
    this.focusTrap.activate();
    if (this.closeButton) {
      this.closeButton.focus();
    }
  }

  async navigate(url) {
    if (!this.modal || !this.isOpen) return;
    await this.replaceContent(url);
  }

  async replaceContent(url) {
    const contentContainer = this.modal.querySelector('.js-project-modal-content');
    const titleElement = this.modal.querySelector('.js-project-modal-title');
    const infoContainer = this.modal.querySelector('.js-project-modal-info');
    
    if (contentContainer) {
      contentContainer.classList.add('is-loading');
      contentContainer.innerHTML = '<div style="text-align: center; padding: 2rem;">Loading...</div>';
      if (infoContainer) {
        infoContainer.innerHTML = '';
      }
      
      const { title, info, content } = await this.loadProject(url);
      
      if (titleElement && title) {
        titleElement.textContent = title.toUpperCase();
        titleElement.classList.toggle('is-long', title.length > 20);
      }
      
      // Insert info overlay back into content (it was removed during extraction)
      if (contentContainer && content && info) {
        let fullContent = content;
        // Find the info-and-media section and prepend the info overlay
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = fullContent;
        const infoAndMediaSection = tempDiv.querySelector('.c-project-info-and-media');
        if (infoAndMediaSection) {
          // Insert info overlay as first child
          const infoDiv = document.createElement('div');
          infoDiv.innerHTML = info;
          const infoOverlay = infoDiv.querySelector('.c-project-modal__info-overlay');
          if (infoOverlay) {
            infoAndMediaSection.insertBefore(infoOverlay, infoAndMediaSection.firstChild);
          }
          fullContent = tempDiv.innerHTML;
        }
        contentContainer.innerHTML = fullContent;
      } else if (contentContainer) {
        contentContainer.innerHTML = content;
      }
      
      // Reinitialize scripts for loaded content
      this.reinitScripts();
      
      // Setup scroll-based title fade
      this.setupTitleFade();

      window.requestAnimationFrame(() => {
        contentContainer.classList.remove('is-loading');
      });
    }

    // Update URL without reloading
    if (history.pushState) {
      history.pushState({ modal: true }, '', url);
    }
  }
  
  setupTitleFade() {
    const titleElement = this.modal.querySelector('.js-project-modal-title');
    if (!titleElement) return;
    
    const handleScroll = () => {
      const scrollTop = this.modal.scrollTop;
      const fadeThreshold = window.innerHeight * 0.2; // 20vh
      
      if (scrollTop > fadeThreshold) {
        titleElement.classList.add('is-scrolled');
      } else {
        titleElement.classList.remove('is-scrolled');
      }
    };
    
    // Remove existing listener if any
    if (this.scrollHandler) {
      this.modal.removeEventListener('scroll', this.scrollHandler);
    }
    
    // Add new scroll listener
    this.scrollHandler = handleScroll;
    this.modal.addEventListener('scroll', this.scrollHandler);
    
    // Check initial scroll position
    handleScroll();
  }

  close() {
    if (!this.modal) return;

    // Remove scroll listener
    if (this.scrollHandler) {
      this.modal.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    // Reset title opacity
    const titleElement = this.modal.querySelector('.js-project-modal-title');
    if (titleElement) {
      titleElement.classList.remove('is-scrolled');
    }

    this.modal.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');
    this.isOpen = false;
    document.body.classList.remove('no-scroll');
    this.focusTrap.deactivate();

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;

    // Restore URL
    if (history.pushState && window.location.pathname.includes('/projects/')) {
      const projectsUrl = window.location.origin + '/projects';
      history.pushState({}, '', projectsUrl);
    }
  }

  reinitScripts() {
    initLazyMedia(this.modal);

    // Reinitialize collapsible text
    const collapsibleTexts = this.modal.querySelectorAll('.js-collapsible-text');
    collapsibleTexts.forEach((element) => {
      const toggle = element.querySelector('.js-collapsible-text-toggle');
      const content = element.querySelector('.js-collapsible-text-content');
      if (toggle && content) {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const isClosed = element.getAttribute('data-state') === 'closed';
          element.setAttribute('data-state', isClosed ? 'open' : 'closed');
          
          if (isClosed) {
            const contentHeight = content.offsetHeight;
            element.style.maxHeight = contentHeight + 'px';
            element.classList.remove('has-gradient');
          } else {
            element.style.maxHeight = '15em';
            element.classList.add('has-gradient');
          }
        });
      }
    });
    
    // Reinitialize lightbox for project images
    const lightboxLinks = this.modal.querySelectorAll('[data-rel="aiLightbox"]');
    if (lightboxLinks.length > 0) {
      const lb = new Lightbox({
        selector: '[data-rel="aiLightbox"]',
        lazyload: true,
        arrows: true,
        counter: true,
        slideSpeed: 500,
      });
    }
    
    // Reinitialize carousels
    initCarousel();
    applyReducedMotionVideos(this.modal);
  }

  destroy() {
    if (this.modal) {
      this.modal.remove();
    }
  }
}

